import { FormProvider, useForm } from 'react-hook-form';

import { UserPreferredUnit } from '@data';
import { useTranslate } from '@i18n';
import { useUpdateUserSettings, useUserName, useUserSettings } from '@queries';
import { activeUserIdAtom, useEnsureAtomValue } from '@store';
import { Button, ButtonProps, Heading3 } from '@ui';

import { StudentGolfBag } from './StudentGolfBag';
import { StudentSettingsRoot } from './StudentSettingsRoot';
import { StudentUnits } from './StudentUnits';

const CancelButton = ({ onClose }: { onClose: () => void }) => {
    const t = useTranslate();
    return (
        <Button variant="secondary" onClick={onClose}>
            {t('generic_ui.cancel')}
        </Button>
    );
};

type SaveButtonProps = Pick<ButtonProps, 'isLoading' | 'isDisabled'> & {
    onClick?: (() => void) | undefined;
};

const SaveButton = ({ onClick, ...rest }: SaveButtonProps) => {
    const t = useTranslate();
    return (
        <Button variant="primary" onClick={onClick} {...rest}>
            {t('generic_ui.save')}
        </Button>
    );
};

type StudentSettingsProps = {
    onClose: () => void;
};

type StudentSettingsForm = {
    userPreferredUnit: UserPreferredUnit;
};

export const StudentSettings = ({ onClose }: StudentSettingsProps) => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    const { data: userSettings, isLoading } = useUserSettings(activeUserId);
    const { data: userName } = useUserName(activeUserId);

    const methods = useForm<StudentSettingsForm>({
        defaultValues: {
            userPreferredUnit: userSettings?.userPreferredUnit ?? 'metric',
        },
    });

    const { mutateAsync: updateUserSettings } = useUpdateUserSettings(activeUserId);

    const handleSave = async ({ userPreferredUnit }: StudentSettingsForm) => {
        await updateUserSettings({ userPreferredUnit: userPreferredUnit });
        onClose?.();
    };

    return (
        <FormProvider {...methods}>
            <StudentSettingsRoot
                buttons={
                    <>
                        <Heading3>Viewing Student: {userName ?? 'N/A'}</Heading3>
                        <CancelButton onClose={onClose} />
                        <SaveButton
                            isLoading={isLoading}
                            isDisabled={isLoading}
                            onClick={methods.handleSubmit(handleSave)}
                        />
                    </>
                }
            >
                <StudentUnits />
                <StudentGolfBag activeUserId={activeUserId} />
            </StudentSettingsRoot>
        </FormProvider>
    );
};
